'use client';

import { Box, Tab, Tabs, Typography, List, ListItem, ListItemText, Chip } from '@mui/material';
import { useState } from 'react';

interface Requirements {
  must: string[];
  preferred: string[];
}

interface JobWithRelations {
  id: string;
  title: string;
  description: string;
  budget: number | null;
  deadline: Date | null;
  type: 'DESIGN' | 'MANUFACTURING';
  status: 'OPEN' | 'IN_PROGRESS' | 'COMPLETED';
  requirements: Requirements;
  categories: string[];
  createdAt: Date;
  updatedAt: Date;
  posterId: string;
  acceptorId: string | null;
  poster: {
    name: string;
  };
  files: Array<{
    id: string;
    name: string;
    url: string;
    size: number;
  }>;
  messages: Array<{
    id: string;
    content: string;
    createdAt: Date;
    sender: {
      name: string;
      id: string;
    };
    file?: {
      id: string;
      name: string;
      url: string;
      size: number;
    };
  }>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`job-tabpanel-${index}`}
      aria-labelledby={`job-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function JobTabs({ job }: { job: JobWithRelations }) {
  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // Parse requirements from JSON if needed
  let requirements: Requirements = { must: [], preferred: [] };
  try {
    if (typeof job.requirements === 'string') {
      requirements = JSON.parse(job.requirements);
    } else if (
      typeof job.requirements === 'object' &&
      job.requirements !== null &&
      'must' in job.requirements &&
      'preferred' in job.requirements
    ) {
      requirements = job.requirements;
    }
  } catch (error) {
    console.error('Error parsing requirements:', error);
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={handleChange} aria-label="job details tabs">
          <Tab label="Requirements" />
          <Tab label="Files" />
          <Tab label="Messages" />
        </Tabs>
      </Box>

      <TabPanel value={value} index={0}>
        <List>
          {requirements.must?.length > 0 && (
            <ListItem>
              <ListItemText
                primary="Must Have Requirements"
                secondary={
                  <Box sx={{ mt: 1 }}>
                    {requirements.must.map((req, index) => (
                      <Chip key={index} label={req} color="primary" sx={{ mr: 1, mb: 1 }} />
                    ))}
                  </Box>
                }
              />
            </ListItem>
          )}
          {requirements.preferred?.length > 0 && (
            <ListItem>
              <ListItemText
                primary="Preferred Requirements"
                secondary={
                  <Box sx={{ mt: 1 }}>
                    {requirements.preferred.map((req, index) => (
                      <Chip key={index} label={req} color="secondary" sx={{ mr: 1, mb: 1 }} />
                    ))}
                  </Box>
                }
              />
            </ListItem>
          )}
          {!requirements.must?.length && !requirements.preferred?.length && (
            <ListItem>
              <ListItemText
                primary="No specific requirements listed"
                secondary="The job creator hasn't specified any requirements."
              />
            </ListItem>
          )}
        </List>
      </TabPanel>

      <TabPanel value={value} index={1}>
        <List>
          {job.files?.length > 0 ? (
            job.files.map(file => (
              <ListItem key={file.id}>
                <ListItemText
                  primary={file.name}
                  secondary={`Size: ${(file.size / 1024 / 1024).toFixed(2)} MB`}
                />
              </ListItem>
            ))
          ) : (
            <ListItem>
              <ListItemText
                primary="No files attached"
                secondary="The job creator hasn't uploaded any files."
              />
            </ListItem>
          )}
        </List>
      </TabPanel>

      <TabPanel value={value} index={2}>
        <List>
          {job.messages?.length > 0 ? (
            job.messages.map(message => (
              <ListItem key={message.id}>
                <ListItemText
                  primary={message.content}
                  secondary={`${message.sender.name} - ${new Date(
                    message.createdAt
                  ).toLocaleString()}`}
                />
              </ListItem>
            ))
          ) : (
            <ListItem>
              <ListItemText
                primary="No messages yet"
                secondary="Be the first to start a conversation about this job."
              />
            </ListItem>
          )}
        </List>
      </TabPanel>
    </Box>
  );
}
